<script setup lang="ts">
import { onMounted, ref, computed, watch } from 'vue'
import Bs2Title from '@/components/common/layout/Bs2Title.vue'
import ButtonDaisy from '@/components/common/buttons/ButtonDaisy.vue'
import SelectInput from '@/components/common/selects/SelectInput.vue'
import SelectInputGrouped from '@/components/common/selects/SelectInputGrouped.vue'
import ConfirmModal from '@/components/common/layout/ConfirmModal.vue'
import GridVue from '@/components/data-visualization/grids/GridVue.vue'
import UsersGridActions from '@/components/features/admin/UsersGridActions.vue'
import AdminNotificationModule from '@/components/features/admin/AdminNotificationModule.vue'
import { useUserAdminStore, type UserAdminItem, type UserAdminManagedItem, type UserPasswordInfo, type UserResetToken, type BrevoEmail, type LuccaLookupResult, type LuccaSyncResult, type OdooSyncResult } from '@/stores/useUserAdminStore'
import { useFeatureFlagAdminStore, type FeatureFlagItem, type UserFeatureFlag } from '@/stores/useFeatureFlagAdminStore'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'
import { useAuthStore } from '@/stores/useAuthStore'
import {
  MagnifyingGlassIcon,
  UserCircleIcon,
  NoSymbolIcon,
  ChevronRightIcon,
  EnvelopeIcon,
  KeyIcon,
  EyeIcon,
  PlusIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from '@heroicons/vue/24/outline'

const adminStore = useUserAdminStore()
const featureFlagStore = useFeatureFlagAdminStore()
const boostCoreStore = useBoostCorePermissionStore()
const authStore = useAuthStore()

type UserStatusFilter = 'ACTIVE' | 'DISABLED' | 'ALL'

const search = ref('')
const filterBu = ref<string | null>(null)
const filterPermission = ref<string | null>(null)
const filterRoleGroup = ref<string | null>(null)
const filterStatus = ref<UserStatusFilter>('ACTIVE')

const STATUS_FILTER_OPTIONS: { id: string; value: UserStatusFilter; label: string }[] = [
  { id: 'ACTIVE', value: 'ACTIVE', label: 'Actifs' },
  { id: 'DISABLED', value: 'DISABLED', label: 'Archivés' },
  { id: 'ALL', value: 'ALL', label: 'Tous' },
]
const page = ref(1)
const pageSize = ref(10)
const selectedUserUuid = ref<string | null>(null)
const showEditModal = ref(false)
const showRevokeModal = ref(false)
const tokenToRevoke = ref<{ userUuid: string; tokenUuid: string } | null>(null)
const showRevokeAllModal = ref(false)
const userToRevokeAll = ref<string | null>(null)
const saving = ref(false)
const activeTab = ref<'general' | 'permissions' | 'tokens' | 'manages' | 'metadata' | 'preferences' | 'password' | 'brevo' | 'featureflags'>('general')

const modalTabs = [
  { id: 'general' as const, label: 'Général' },
  { id: 'password' as const, label: 'Mot de passe' },
  { id: 'brevo' as const, label: 'Emails Brevo' },
  { id: 'permissions' as const, label: 'Groupes & permissions' },
  { id: 'featureflags' as const, label: 'Feature flags' },
  { id: 'manages' as const, label: 'Managés' },
  { id: 'tokens' as const, label: 'Access tokens' },
  { id: 'metadata' as const, label: 'Metadata' },
  { id: 'preferences' as const, label: 'Préférences' },
]

const userFeatureFlags = ref<UserFeatureFlag[]>([])
const allFeatureFlags = ref<FeatureFlagItem[]>([])
const isLoadingFeatureFlags = ref(false)
const selectedFlagToAssign = ref<string | null>(null)
const assigningFlag = ref(false)
const featureFlagError = ref<string | null>(null)

const passwordInfo = ref<UserPasswordInfo | null>(null)
const resetTokens = ref<UserResetToken[]>([])
const brevoEmails = ref<BrevoEmail[]>([])
const brevoCount = ref(0)
const isLoadingPassword = ref(false)
const isLoadingBrevo = ref(false)
const isLoadingResetTokens = ref(false)
const sendingReset = ref(false)
const resetSent = ref(false)

async function loadTabData(tab: string) {
  if (!currentUser.value) return
  if (tab === 'password') {
    isLoadingPassword.value = true
    isLoadingResetTokens.value = true
    passwordInfo.value = await adminStore.fetchPasswordInfo(currentUser.value.uuid)
    resetTokens.value = await adminStore.fetchResetTokens(currentUser.value.uuid)
    isLoadingPassword.value = false
    isLoadingResetTokens.value = false
  } else if (tab === 'brevo' && currentUser.value.email) {
    isLoadingBrevo.value = true
    const res = await adminStore.fetchBrevoEmails(currentUser.value.email, { limit: 50 })
    brevoEmails.value = res.emails
    brevoCount.value = res.count
    isLoadingBrevo.value = false
  } else if (tab === 'featureflags') {
    isLoadingFeatureFlags.value = true
    featureFlagError.value = null
    const [userFlags, all] = await Promise.all([
      featureFlagStore.fetchFlagsForUser(currentUser.value.uuid),
      featureFlagStore.fetchFlags(),
    ])
    userFeatureFlags.value = userFlags
    allFeatureFlags.value = all
    selectedFlagToAssign.value = null
    isLoadingFeatureFlags.value = false
  }
}

const availableFlagsToAssign = computed(() => {
  const assigned = new Set(userFeatureFlags.value.map((f) => f.featureFlagUuid))
  return allFeatureFlags.value
    .filter((f) => !assigned.has(f.uuid) && f.status === 'ACTIVE')
    .map((f) => ({ id: f.uuid, value: f.uuid, label: f.code }))
})

async function assignFeatureFlag() {
  if (!currentUser.value || !selectedFlagToAssign.value) return
  assigningFlag.value = true
  featureFlagError.value = null
  try {
    const created = await featureFlagStore.assignFlagToUser(selectedFlagToAssign.value, currentUser.value.uuid)
    if (created) {
      userFeatureFlags.value = [created, ...userFeatureFlags.value]
      selectedFlagToAssign.value = null
      // Mise à jour du compteur côté liste
      const idx = featureFlagStore.flags.findIndex((f) => f.uuid === created.featureFlagUuid)
      if (idx >= 0) {
        featureFlagStore.flags[idx].userCount += 1
        featureFlagStore.flags[idx].assignmentsCount += 1
      }
    } else {
      featureFlagError.value = "Erreur lors de l'affectation du feature flag"
    }
  } finally {
    assigningFlag.value = false
  }
}

async function unassignFeatureFlag(featureFlagUuid: string) {
  if (!currentUser.value) return
  const ok = await featureFlagStore.unassignFlagFromUser(featureFlagUuid, currentUser.value.uuid)
  if (ok) {
    userFeatureFlags.value = userFeatureFlags.value.filter((f) => f.featureFlagUuid !== featureFlagUuid)
    const idx = featureFlagStore.flags.findIndex((f) => f.uuid === featureFlagUuid)
    if (idx >= 0) {
      featureFlagStore.flags[idx].userCount = Math.max(0, featureFlagStore.flags[idx].userCount - 1)
      featureFlagStore.flags[idx].assignmentsCount = Math.max(0, featureFlagStore.flags[idx].assignmentsCount - 1)
    }
  } else {
    featureFlagError.value = 'Erreur lors du retrait du feature flag'
  }
}

async function sendResetToUser() {
  if (!currentUser.value) return
  sendingReset.value = true
  resetSent.value = false
  const ok = await adminStore.sendResetEmail(currentUser.value.uuid)
  sendingReset.value = false
  if (ok) {
    resetSent.value = true
    // Refresh reset tokens list
    resetTokens.value = await adminStore.fetchResetTokens(currentUser.value.uuid)
  }
}

watch(activeTab, (tab) => loadTabData(tab))
const errorMsg = ref<string | null>(null)

const currentUser = computed(() => adminStore.currentUser)
const users = computed(() => adminStore.users)
const total = computed(() => adminStore.total)
const permissions = computed(() => adminStore.permissions)
const roleGroups = computed(() => adminStore.roleGroups)
const agenciesGrouped = computed(() => adminStore.agenciesGrouped)
const userRoleGroups = computed(() => currentUser.value?.roleGroups ?? [])
const availableRoleGroups = computed(() => {
  const assigned = new Set(userRoleGroups.value.map((g) => g.uuid))
  return roleGroups.value.filter((g) => !assigned.has(g.uuid))
})

const BU_OPTIONS = computed(() => [
  { id: 'none', value: null, label: '—' } as { id: string; value: string | null; label: string },
  ...adminStore.bus,
])

const permissionFilterOptions = computed(() => {
  const opts: { id: string; value: string | null; label: string }[] = [{ id: 'none', value: null, label: 'Toutes' }]
  permissions.value.forEach((p) => {
    opts.push({ id: p.code, value: p.code, label: p.code })
  })
  return opts
})

const roleGroupFilterOptions = computed(() => {
  const opts: { id: string; value: string | null; label: string }[] = [{ id: 'none', value: null, label: 'Tous' }]
  roleGroups.value.forEach((g) => {
    opts.push({ id: g.uuid, value: g.uuid, label: g.name })
  })
  return opts
})

const totalPages = computed(() => Math.max(1, Math.ceil(total.value / pageSize.value)))

const gridColumns = computed(() => [
  {
    field: 'userLabel',
    headerName: 'Utilisateur',
    valueGetter: (params: any) => adminStore.getUserLabel(params.data),
    filter: false,
  },
  {
    field: 'email',
    headerName: 'Email',
    filter: false,
  },
  {
    field: 'bu',
    headerName: 'BU',
    valueFormatter: (params: any) => params.value || '—',
    filter: false,
  },
  {
    field: 'managerEmail',
    headerName: 'Manager',
    valueFormatter: (params: any) => params.value || '—',
    filter: false,
  },
  {
    field: 'agencyName',
    headerName: 'Agence',
    valueFormatter: (params: any) => params.value || '—',
    filter: false,
  },
  {
    field: 'permissions',
    headerName: 'Permissions',
    valueFormatter: (params: any) => {
      const arr = params.value ?? []
      if (arr.length === 0) return '—'
      const first = arr.slice(0, 5).join(', ')
      return arr.length > 5 ? `${first} +${arr.length - 5}` : first
    },
    filter: false,
  },
  {
    field: 'status',
    headerName: 'Statut',
    cellClass: (params: any) =>
      params.value === 'ACTIVE' ? 'text-success' : 'text-error',
    filter: false,
  },
  {
    field: 'actions',
    headerName: 'Actions',
    resizable: false,
    filter: false,
    minWidth: 110,
    maxWidth: 110,
    cellRenderer: UsersGridActions,
    cellRendererParams: { onClickEdit: openUser, onClickImpersonate: loginAsRow, canImpersonate },
    sortable: false,
    cellClass: 'border-0 grid-action',
  },
])

const managerOptions = computed(() => {
  const opts: { id: string; value: string | null; label: string }[] = [{ id: 'none', value: null, label: '—' }]
  adminStore.usersForManagers.forEach((u) => {
    if (u.uuid !== currentUser.value?.uuid) {
      opts.push({
        id: u.uuid,
        value: u.uuid,
        label: adminStore.getUserLabel(u),
      })
    }
  })
  return opts
})

const addManagedUserOptions = computed(() => {
  const managedUuids = new Set(currentUser.value?.managedUsers?.map((m) => m.uuid) ?? [])
  return adminStore.usersForManagers
    .filter((u) => u.uuid !== currentUser.value?.uuid && !managedUuids.has(u.uuid))
    .map((u) => ({ id: u.uuid, value: u.uuid, label: adminStore.getUserLabel(u) }))
})

const selectedUserToAddAsManaged = ref<string | null>(null)

const editForm = ref({
  status: '' as string,
  bu: null as string | null,
  managerUuid: null as string | null,
  agencyUuid: null as string | null,
  payplanLevel: null as 'JUNIOR' | 'SENIOR' | null,
})

// BU rémunérées au modèle « paliers » (barème Junior/Sénior). UNIXO_VAD à ajouter ultérieurement.
const PALIER_BUS = ['UNIXO_ACR']
const isPalierBu = computed(() => !!editForm.value.bu && PALIER_BUS.includes(editForm.value.bu))

watch(currentUser, (u) => {
  if (u) {
    editForm.value = {
      status: u.status,
      bu: u.bu,
      managerUuid: u.managerUuid,
      agencyUuid: u.agencyUuid,
      payplanLevel: ((u.metadata as Record<string, unknown> | null)?.payplanLevel as 'JUNIOR' | 'SENIOR' | undefined) ?? null,
    }
  }
}, { immediate: true })

async function loadUsers() {
  await adminStore.fetchUsers({
    limit: pageSize.value,
    offset: (page.value - 1) * pageSize.value,
    orderBy: 'dateCreated',
    order: 'desc',
    search: search.value || undefined,
    bu: filterBu.value || undefined,
    permission: filterPermission.value || undefined,
    roleGroupUuid: filterRoleGroup.value || undefined,
    status: filterStatus.value === 'ALL' ? undefined : filterStatus.value,
  })
}

async function loadPermissions() {
  await adminStore.fetchPermissions()
}

function openUser(u: UserAdminItem | UserAdminManagedItem) {
  selectedUserUuid.value = u.uuid
  adminStore.fetchUserDetail(u.uuid)
  showEditModal.value = true
  activeTab.value = 'general'
}

function closeModal() {
  showEditModal.value = false
  selectedUserUuid.value = null
  selectedUserToAddAsManaged.value = null
  activeTab.value = 'general'
  passwordInfo.value = null
  resetTokens.value = []
  brevoEmails.value = []
  brevoCount.value = 0
  resetSent.value = false
  luccaSyncResult.value = null
  luccaSyncError.value = null
  userFeatureFlags.value = []
  allFeatureFlags.value = []
  selectedFlagToAssign.value = null
  featureFlagError.value = null
}

async function saveUser() {
  if (!currentUser.value) return
  saving.value = true
  errorMsg.value = null
  try {
    const ok = await adminStore.updateUser(currentUser.value.uuid, {
      status: editForm.value.status as 'ACTIVE' | 'DISABLED',
      bu: editForm.value.bu,
      managerUuid: editForm.value.managerUuid,
      agencyUuid: editForm.value.agencyUuid,
      // N'envoyer le barème que pour les BU à paliers (sinon non pertinent).
      payplanLevel: isPalierBu.value ? editForm.value.payplanLevel : undefined,
    })
    if (ok) closeModal()
    else errorMsg.value = 'Erreur lors de la mise à jour'
  } finally {
    saving.value = false
  }
}

async function addGroup(roleGroupUuid: string) {
  if (!currentUser.value) return
  const ok = await adminStore.addRoleGroup(currentUser.value.uuid, roleGroupUuid)
  if (!ok) errorMsg.value = "Erreur lors de l'affectation du groupe"
}

async function removeGroup(roleGroupUuid: string) {
  if (!currentUser.value) return
  const ok = await adminStore.removeRoleGroup(currentUser.value.uuid, roleGroupUuid)
  if (!ok) errorMsg.value = "Erreur lors de la désaffectation du groupe"
}

async function addManagedUser() {
  if (!currentUser.value || !selectedUserToAddAsManaged.value) return
  const ok = await adminStore.updateUser(selectedUserToAddAsManaged.value, {
    managerUuid: currentUser.value.uuid,
  })
  if (ok) {
    selectedUserToAddAsManaged.value = null
    await adminStore.fetchUserDetail(currentUser.value.uuid)
  } else {
    errorMsg.value = 'Erreur lors de l\'ajout du managé'
  }
}

function askRevokeToken(userUuid: string, tokenUuid: string) {
  tokenToRevoke.value = { userUuid, tokenUuid }
  showRevokeModal.value = true
}

async function confirmRevokeToken() {
  if (!tokenToRevoke.value) return
  await adminStore.revokeAccessToken(tokenToRevoke.value.userUuid, tokenToRevoke.value.tokenUuid)
  showRevokeModal.value = false
  tokenToRevoke.value = null
}

function askRevokeAllTokens(userUuid: string) {
  userToRevokeAll.value = userUuid
  showRevokeAllModal.value = true
}

async function confirmRevokeAllTokens() {
  if (!userToRevokeAll.value) return
  await adminStore.revokeAllAccessTokens(userToRevokeAll.value)
  showRevokeAllModal.value = false
  userToRevokeAll.value = null
}

const tricksterLoading = ref(false)

const luccaSyncLoading = ref(false)
const luccaSyncResult = ref<LuccaSyncResult | null>(null)
const luccaSyncError = ref<string | null>(null)
const luccaDisableLoading = ref(false)
const luccaManagerApplyLoading = ref(false)
const luccaManagerApplyError = ref<string | null>(null)
const luccaManagedSelected = ref<Record<string, boolean>>({})
const luccaManagedApplyLoading = ref(false)
const luccaManagedApplyError = ref<string | null>(null)

const odooSyncLoading = ref(false)
const odooSyncResult = ref<OdooSyncResult | null>(null)
const odooSyncError = ref<string | null>(null)

watch(currentUser, (u, prev) => {
  if (u?.uuid !== prev?.uuid) {
    luccaSyncResult.value = null
    luccaSyncError.value = null
    luccaManagerApplyError.value = null
    luccaManagedSelected.value = {}
    luccaManagedApplyError.value = null
    odooSyncResult.value = null
    odooSyncError.value = null
  }
})

async function syncLucca() {
  if (!currentUser.value) return
  luccaSyncLoading.value = true
  luccaSyncError.value = null
  try {
    const res = await adminStore.syncUserFromLucca(currentUser.value.uuid)
    if (!res) {
      luccaSyncError.value = 'Erreur lors de la synchronisation Lucca'
      return
    }
    luccaSyncResult.value = res
  } finally {
    luccaSyncLoading.value = false
  }
}

async function applyLuccaManager() {
  if (!currentUser.value || !luccaSyncResult.value?.managerProposal?.proposedManagerUuid) return
  luccaManagerApplyLoading.value = true
  luccaManagerApplyError.value = null
  try {
    const res = await adminStore.applyLuccaManager(
      currentUser.value.uuid,
      luccaSyncResult.value.managerProposal.proposedManagerUuid,
    )
    if (!res) {
      luccaManagerApplyError.value = 'Erreur lors de l\'application du manager Lucca'
      return
    }
    if (luccaSyncResult.value.managerProposal) {
      luccaSyncResult.value.managerProposal.status = 'already_correct'
      luccaSyncResult.value.managerProposal.currentManagerUuid = res.managerUuid
    }
  } finally {
    luccaManagerApplyLoading.value = false
  }
}

async function applyLuccaManaged() {
  if (!currentUser.value || !luccaSyncResult.value?.managedProposal) return
  const uuidsToAdd = luccaSyncResult.value.managedProposal.toAdd
    .filter((u) => luccaManagedSelected.value[u.uuid])
    .map((u) => u.uuid)
  if (uuidsToAdd.length === 0) return
  luccaManagedApplyLoading.value = true
  luccaManagedApplyError.value = null
  try {
    const res = await adminStore.applyLuccaManaged(currentUser.value.uuid, uuidsToAdd)
    if (!res?.ok) {
      luccaManagedApplyError.value = 'Erreur lors de l\'affectation des managés'
      return
    }
    // Retirer les éléments appliqués de la liste affichée
    if (luccaSyncResult.value.managedProposal) {
      luccaSyncResult.value.managedProposal.toAdd =
        luccaSyncResult.value.managedProposal.toAdd.filter((u) => !uuidsToAdd.includes(u.uuid))
      if (
        luccaSyncResult.value.managedProposal.toAdd.length === 0 &&
        luccaSyncResult.value.managedProposal.toRemove.length === 0 &&
        luccaSyncResult.value.managedProposal.notInBoostcore.length === 0
      ) {
        luccaSyncResult.value.managedProposal.status = 'all_correct'
      }
    }
    luccaManagedSelected.value = {}
  } finally {
    luccaManagedApplyLoading.value = false
  }
}

async function syncOdoo() {
  if (!currentUser.value) return
  odooSyncLoading.value = true
  odooSyncError.value = null
  try {
    const res = await adminStore.syncUserFromOdoo(currentUser.value.uuid)
    if (!res) {
      odooSyncError.value = 'Erreur lors de la synchronisation Odoo'
      return
    }
    odooSyncResult.value = res
  } finally {
    odooSyncLoading.value = false
  }
}

async function disableFromLucca() {
  if (!currentUser.value) return
  luccaDisableLoading.value = true
  try {
    const ok = await adminStore.updateUser(currentUser.value.uuid, { status: 'DISABLED' })
    if (ok) {
      editForm.value.status = 'DISABLED'
      luccaSyncResult.value = null
    }
  } finally {
    luccaDisableLoading.value = false
  }
}

async function loginAsUser() {
  if (!currentUser.value) return
  tricksterLoading.value = true
  try {
    const result = await adminStore.generateTricksterToken(currentUser.value.uuid)
    if (result) {
      authStore.startTrickster(result)
      window.location.href = '/'
    }
  } finally {
    tricksterLoading.value = false
  }
}

/** Peut-on se connecter en tant que cet utilisateur (USER_MASTER + pas soi-même) ? */
function canImpersonate(u: UserAdminItem): boolean {
  return boostCoreStore.include('USER_MASTER') && u.uuid !== boostCoreStore.user?.uuid
}

/** « Se connecter en tant que » directement depuis une ligne du listing. */
async function loginAsRow(u: UserAdminItem) {
  if (!canImpersonate(u)) return
  tricksterLoading.value = true
  try {
    const result = await adminStore.generateTricksterToken(u.uuid)
    if (result) {
      authStore.startTrickster(result)
      window.location.href = '/'
    }
  } finally {
    tricksterLoading.value = false
  }
}

function formatDate(s: string | null) {
  if (!s) return '—'
  return new Date(s).toLocaleString('fr-FR')
}

const metadataJson = computed(() => {
  const m = currentUser.value?.metadata
  if (!m || (typeof m === 'object' && Object.keys(m).length === 0)) return null
  return JSON.stringify(m, null, 2)
})

const preferencesJson = computed(() => {
  const m = currentUser.value?.preferences
  if (!m || (typeof m === 'object' && Object.keys(m).length === 0)) return null
  return JSON.stringify(m, null, 2)
})

function isTokenRevoked(t: { revokedAt: string | null }) {
  return !!t.revokedAt
}

function isTokenActive(t: { revokedAt: string | null; expiresAt: string | null }) {
  if (t.revokedAt) return false
  if (t.expiresAt && new Date(t.expiresAt).getTime() <= Date.now()) return false
  return true
}

function tokenStatus(t: { revokedAt: string | null; expiresAt: string | null }) {
  if (t.revokedAt) return 'Révoqué'
  if (t.expiresAt && new Date(t.expiresAt).getTime() <= Date.now()) return 'Expiré'
  return 'Actif'
}

onMounted(async () => {
  if (!boostCoreStore.user) await boostCoreStore.fetchMe()
  await Promise.all([loadPermissions(), adminStore.fetchRoleGroups(), adminStore.fetchAgenciesGrouped(), adminStore.fetchBus()])
  await adminStore.fetchUsersForManagers()
  await loadUsers()
})

watch([search, filterBu, filterPermission, filterRoleGroup, filterStatus, pageSize], () => { page.value = 1 })
watch([search, filterBu, filterPermission, filterRoleGroup, filterStatus, page, pageSize], () => loadUsers(), { immediate: false })

async function onPaginationChanged(newPageSize: number, newPage: number) {
  if (pageSize.value !== newPageSize) pageSize.value = newPageSize
  if (page.value !== newPage) page.value = newPage
  await loadUsers()
}

const showAddModal = ref(false)
const addEmail = ref('')
const addFirstName = ref('')
const addLastName = ref('')
const lookupResult = ref<LuccaLookupResult | null>(null)
const lookupError = ref<string | null>(null)
const createError = ref<string | null>(null)
const isLookingUp = ref(false)
const isCreating = ref(false)

function openAddModal() {
  showAddModal.value = true
  addEmail.value = ''
  addFirstName.value = ''
  addLastName.value = ''
  lookupResult.value = null
  lookupError.value = null
  createError.value = null
}

function closeAddModal() {
  showAddModal.value = false
  addEmail.value = ''
  addFirstName.value = ''
  addLastName.value = ''
  lookupResult.value = null
  lookupError.value = null
  createError.value = null
}

async function doLookupLucca() {
  if (!addEmail.value.trim()) return
  isLookingUp.value = true
  lookupError.value = null
  createError.value = null
  try {
    const res = await adminStore.lookupLuccaUser(addEmail.value.trim())
    if (!res) {
      lookupError.value = 'Erreur lors de la recherche Lucca'
      return
    }
    lookupResult.value = res
    if (res.foundOnLucca) {
      addFirstName.value = res.firstName ?? ''
      addLastName.value = res.lastName ?? ''
    }
  } finally {
    isLookingUp.value = false
  }
}

function openExistingUserFromLookup() {
  const existing = lookupResult.value?.existingUser
  if (!existing) return
  const asUser = {
    uuid: existing.uuid,
    email: existing.email,
    firstName: existing.firstName,
    lastName: existing.lastName,
    status: existing.status,
    bu: null,
    managerUuid: null,
  } as unknown as UserAdminItem
  closeAddModal()
  openUser(asUser)
}

async function doCreateUser() {
  if (!addEmail.value.trim()) return
  isCreating.value = true
  createError.value = null
  try {
    const dto = {
      email: addEmail.value.trim(),
      firstName: addFirstName.value.trim() || null,
      lastName: addLastName.value.trim() || null,
    }
    const res = await adminStore.createUser(dto)
    if (res.ok) {
      closeAddModal()
      await loadUsers()
      openUser({ uuid: res.user.uuid } as UserAdminItem)
    } else {
      createError.value = res.message ?? 'Erreur lors de la création'
    }
  } finally {
    isCreating.value = false
  }
}
</script>

<template>
  <div class="p-4 md:p-6">
    <Bs2Title title="Administration utilisateurs" subtitle="USER_MASTER uniquement" />
    <div class="flex flex-wrap gap-2 mt-4">
      <router-link
        v-if="boostCoreStore.include('SUPER_ADMIN')"
        to="/dashboard/admin"
        class="btn btn-primary btn-sm"
      >
        ← Dashboard admin
      </router-link>
      <router-link to="/admin/regions" class="btn btn-outline btn-sm">
        Régions
      </router-link>
      <router-link to="/admin/teams" class="btn btn-outline btn-sm">
        Équipes
      </router-link>
      <router-link to="/admin/agencies" class="btn btn-outline btn-sm">
        Agences
      </router-link>
      <router-link
        v-if="boostCoreStore.include('SUPER_ADMIN')"
        to="/dashboard/admin/featureflag"
        class="btn btn-outline btn-sm"
      >
        Feature flags
      </router-link>
      <router-link
        v-if="boostCoreStore.include('SUPER_ADMIN')"
        to="/dashboard/admin/access-control"
        class="btn btn-outline btn-sm"
      >
        Permissions & groupes
      </router-link>
    </div>
    <div class="mt-8 card hidden bg-base-100 shadow md:block">
      <div class="card-body">
        <GridVue
          :list="users"
          :cols="gridColumns"
          :use-server-side-pagination="true"
          :pagination-page-size="pageSize"
          :pagination-total-rows="total"
          :current-page="page"
          :total-pages="totalPages"
          @onPaginationChanged="onPaginationChanged"
        >
          <template #title>
            <h2 class="card-title">Utilisateurs ({{ total }})</h2>
          </template>
          <template #filter>
            <div class="flex flex-wrap items-end gap-2">
              <div class="form-control">
                <div class="join">
                  <input
                    v-model="search"
                    type="text"
                    placeholder="Rechercher (email, nom…)"
                    class="input input-bordered input-sm w-48 join-item"
                  />
                  <button class="btn btn-square btn-sm join-item" @click="loadUsers">
                    <MagnifyingGlassIcon class="w-4 h-4" />
                  </button>
                </div>
              </div>
              <SelectInput
                v-model="filterStatus"
                label="Statut"
                :options="STATUS_FILTER_OPTIONS"
                class="min-w-40"
              />
              <SelectInput
                v-model="filterBu"
                label="BU"
                :options="BU_OPTIONS"
                class="min-w-40"
              />
              <SelectInput
                v-model="filterPermission"
                label="Permission"
                :options="permissionFilterOptions"
                placeholder="Toutes"
                class="min-w-40"
              />
              <SelectInput
                v-model="filterRoleGroup"
                label="Groupe"
                :options="roleGroupFilterOptions"
                placeholder="Tous"
                class="min-w-40"
              />
              <ButtonDaisy
                text="Actualiser"
                variant="outline"
                size="sm"
                @click="loadUsers"
              />
              <button class="btn btn-primary btn-sm gap-1" @click="openAddModal">
                <PlusIcon class="w-4 h-4" />
                Ajouter
              </button>
            </div>
          </template>
        </GridVue>
      </div>
    </div>

    <!-- Mobile (< md) : la grille AG est inutilisable au doigt → filtres + cartes + pagination -->
    <div class="mt-6 card bg-base-100 shadow md:hidden">
      <div class="card-body gap-3 p-4">
        <h2 class="card-title text-base">Utilisateurs ({{ total }})</h2>

        <!-- Filtres -->
        <div class="flex flex-col gap-2">
          <div class="join">
            <input
              v-model="search"
              type="text"
              placeholder="Rechercher (email, nom…)"
              class="input input-bordered input-sm join-item flex-1 text-base"
            />
            <button class="btn btn-square btn-sm join-item" aria-label="Rechercher" @click="loadUsers">
              <MagnifyingGlassIcon class="h-4 w-4" />
            </button>
          </div>
          <div class="grid grid-cols-2 gap-2">
            <SelectInput v-model="filterStatus" label="Statut" :options="STATUS_FILTER_OPTIONS" />
            <SelectInput v-model="filterBu" label="BU" :options="BU_OPTIONS" />
            <SelectInput v-model="filterPermission" label="Permission" :options="permissionFilterOptions" placeholder="Toutes" />
            <SelectInput v-model="filterRoleGroup" label="Groupe" :options="roleGroupFilterOptions" placeholder="Tous" />
          </div>
          <button class="btn btn-primary btn-sm gap-1" @click="openAddModal">
            <PlusIcon class="h-4 w-4" />
            Ajouter un utilisateur
          </button>
        </div>

        <!-- Liste -->
        <ul role="list" class="flex flex-col gap-2">
          <li v-if="users.length === 0" class="rounded-lg border border-base-200 py-6 text-center text-base-content/70">
            Aucun utilisateur.
          </li>
          <li v-for="u in users" :key="u.uuid" class="rounded-lg border border-base-200 p-3">
            <div class="flex items-start justify-between gap-2">
              <div class="min-w-0">
                <p class="font-medium text-base-content">{{ adminStore.getUserLabel(u) }}</p>
                <p class="break-all text-sm text-base-content/70">{{ u.email }}</p>
              </div>
              <span
                class="badge badge-sm shrink-0"
                :class="u.status === 'ACTIVE' ? 'badge-success' : 'badge-error'"
              >
                {{ u.status === 'ACTIVE' ? 'Actif' : 'Archivé' }}
              </span>
            </div>
            <dl class="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
              <div class="flex flex-col">
                <dt class="text-xs uppercase tracking-wide text-base-content/70">BU</dt>
                <dd class="text-base-content">{{ u.bu || '—' }}</dd>
              </div>
              <div class="flex flex-col">
                <dt class="text-xs uppercase tracking-wide text-base-content/70">Agence</dt>
                <dd class="text-base-content">{{ u.agencyName || '—' }}</dd>
              </div>
              <div class="col-span-2 flex flex-col">
                <dt class="text-xs uppercase tracking-wide text-base-content/70">Manager</dt>
                <dd class="break-all text-base-content">{{ u.managerEmail || '—' }}</dd>
              </div>
            </dl>
            <div class="mt-2 flex flex-wrap gap-1 border-t border-base-200 pt-2">
              <button class="btn btn-sm btn-primary gap-1" @click="openUser(u)">Modifier</button>
              <button
                v-if="canImpersonate(u)"
                class="btn btn-sm btn-ghost gap-1"
                :disabled="tricksterLoading"
                @click="loginAsRow(u)"
              >
                <EyeIcon class="h-4 w-4" />
                En tant que
              </button>
            </div>
          </li>
        </ul>

        <!-- Pagination -->
        <div class="flex items-center justify-between gap-3">
          <span class="text-sm text-base-content/70">Page {{ page }} / {{ totalPages }}</span>
          <div class="join">
            <button
              class="btn btn-sm join-item"
              :disabled="page <= 1"
              aria-label="Page précédente"
              @click="onPaginationChanged(pageSize, page - 1)"
            >
              <ChevronRightIcon class="h-4 w-4 rotate-180" />
            </button>
            <button
              class="btn btn-sm join-item"
              :disabled="page >= totalPages"
              aria-label="Page suivante"
              @click="onPaginationChanged(pageSize, page + 1)"
            >
              <ChevronRightIcon class="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <AdminNotificationModule />
  </div>

  <div class="modal" :class="{ 'modal-open': showEditModal }">
    <div class="modal-box max-w-3xl">
      <div class="flex items-center justify-between">
        <div>
          <h3 class="font-bold text-lg flex items-center gap-2">
            <UserCircleIcon class="w-6 h-6" />
            {{ currentUser ? adminStore.getUserLabel(currentUser) : '' }}
          </h3>
          <p v-if="currentUser" class="text-sm text-base-content/70">{{ currentUser.email }}</p>
        </div>
        <button
          v-if="currentUser && boostCoreStore.include('USER_MASTER') && currentUser.uuid !== boostCoreStore.user?.uuid"
          class="btn btn-warning btn-sm gap-1"
          :disabled="tricksterLoading"
          @click="loginAsUser"
        >
          <EyeIcon class="w-4 h-4" />
          {{ tricksterLoading ? 'Connexion...' : 'En tant que' }}
        </button>
      </div>

      <div v-if="currentUser" class="mt-4">
        <div v-if="errorMsg" class="alert alert-error mb-4">{{ errorMsg }}</div>

        <div role="tablist" class="tabs tabs-boxed tabs-sm mb-4 flex-nowrap overflow-x-auto p-1 bg-base-200 rounded-lg">
          <a
            v-for="tab in modalTabs"
            :key="tab.id"
            role="tab"
            :class="{
              'tab px-4 py-2 rounded-md font-medium': true,
              'tab-active bg-base-100 shadow-sm': activeTab === tab.id,
            }"
            @click="activeTab = tab.id"
          >
            {{ tab.label }}
          </a>
        </div>

        <div v-show="activeTab === 'general'" class="space-y-4">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SelectInput
              v-model="editForm.status"
              label="Statut"
              :options="[
                { id: 'ACTIVE', value: 'ACTIVE', label: 'ACTIVE' },
                { id: 'DISABLED', value: 'DISABLED', label: 'DISABLED' },
              ]"
            />
            <SelectInput v-model="editForm.bu" label="BU" :options="BU_OPTIONS" />
            <SelectInput
              v-if="isPalierBu"
              v-model="editForm.payplanLevel"
              label="Barème (paliers ACR)"
              :options="[
                { id: 'JUNIOR', value: 'JUNIOR', label: 'Junior (défaut)' },
                { id: 'SENIOR', value: 'SENIOR', label: 'Sénior' },
              ]"
            />
            <SelectInput
              v-model="editForm.managerUuid"
              label="Manager"
              :options="managerOptions"
              class="md:col-span-2"
            />
            <SelectInputGrouped
              v-model="editForm.agencyUuid"
              label="Agence"
              :options="agenciesGrouped"
              placeholder="Sélectionner une agence…"
              class="md:col-span-2"
            />
          </div>

          <div class="border-t pt-4 mt-4 space-y-3">
            <div class="flex items-center justify-between gap-2">
              <h4 class="font-medium text-sm">Synchronisation Lucca</h4>
              <button
                class="btn btn-outline btn-sm gap-1"
                :disabled="luccaSyncLoading"
                @click="syncLucca"
              >
                <ArrowPathIcon class="w-4 h-4" :class="{ 'animate-spin': luccaSyncLoading }" />
                {{ luccaSyncLoading ? 'Synchronisation…' : 'Sync Lucca' }}
              </button>
            </div>

            <div v-if="luccaSyncError" class="alert alert-error text-sm">{{ luccaSyncError }}</div>

            <template v-if="luccaSyncResult">
              <div
                v-if="luccaSyncResult.foundOnLucca && luccaSyncResult.stillEmployed"
                class="alert alert-success text-sm"
              >
                <CheckCircleIcon class="w-5 h-5" />
                <div>
                  <p class="font-medium">Utilisateur synchronisé avec Lucca</p>
                  <p v-if="luccaSyncResult.luccaUser">
                    {{ luccaSyncResult.luccaUser.firstName }} {{ luccaSyncResult.luccaUser.lastName }}
                    <span v-if="luccaSyncResult.luccaUser.employeeNumber" class="text-base-content/60">
                      (matricule {{ luccaSyncResult.luccaUser.employeeNumber }})
                    </span>
                  </p>
                </div>
              </div>

              <div
                v-else-if="luccaSyncResult.foundOnLucca && !luccaSyncResult.stillEmployed"
                class="alert alert-warning text-sm"
              >
                <ExclamationTriangleIcon class="w-5 h-5" />
                <div class="flex-1">
                  <p class="font-medium">Contrat terminé sur Lucca</p>
                  <p>
                    Fin de contrat : {{ formatDate(luccaSyncResult.contractEndDate) }}
                  </p>
                </div>
                <button
                  v-if="currentUser?.status !== 'DISABLED'"
                  class="btn btn-sm btn-error"
                  :disabled="luccaDisableLoading"
                  @click="disableFromLucca"
                >
                  {{ luccaDisableLoading ? 'Désactivation…' : 'Désactiver le compte' }}
                </button>
              </div>

              <div
                v-else
                class="alert alert-error text-sm"
              >
                <XCircleIcon class="w-5 h-5" />
                <div class="flex-1">
                  <p class="font-medium">Utilisateur non trouvé sur Lucca</p>
                  <p>Il a peut-être quitté la société.</p>
                </div>
                <button
                  v-if="currentUser?.status !== 'DISABLED'"
                  class="btn btn-sm btn-error"
                  :disabled="luccaDisableLoading"
                  @click="disableFromLucca"
                >
                  {{ luccaDisableLoading ? 'Désactivation…' : 'Désactiver le compte' }}
                </button>
              </div>

              <!-- Proposition de changement de manager basée sur Lucca -->
              <div
                v-if="luccaSyncResult.managerProposal?.status === 'change_proposed'"
                class="alert alert-warning text-sm"
              >
                <ExclamationTriangleIcon class="w-5 h-5" />
                <div class="flex-1">
                  <p class="font-medium">Manager Lucca différent du manager actuel</p>
                  <p>
                    Lucca indique :
                    <span class="font-semibold">{{ luccaSyncResult.managerProposal.proposedManagerName }}</span>
                    <span class="text-base-content/60"> ({{ luccaSyncResult.managerProposal.proposedManagerEmail }})</span>
                  </p>
                </div>
                <button
                  class="btn btn-sm btn-primary"
                  :disabled="luccaManagerApplyLoading"
                  @click="applyLuccaManager"
                >
                  {{ luccaManagerApplyLoading ? 'Application…' : 'Appliquer' }}
                </button>
              </div>
              <div
                v-else-if="luccaSyncResult.managerProposal?.status === 'manager_not_in_boostcore'"
                class="alert alert-info text-sm"
              >
                <InformationCircleIcon class="w-5 h-5" />
                <div>
                  <p class="font-medium">Manager Lucca non synchronisé sur boostcore</p>
                  <p>
                    Lucca indique
                    <span class="font-semibold">{{ luccaSyncResult.managerProposal.luccaManagerName ?? luccaSyncResult.managerProposal.luccaManagerEmail }}</span>,
                    qui n'a pas de compte boostcore. Crée d'abord ce user pour pouvoir l'assigner.
                  </p>
                </div>
              </div>
              <div
                v-else-if="luccaSyncResult.managerProposal?.status === 'already_correct'"
                class="alert alert-success text-sm"
              >
                <CheckCircleIcon class="w-5 h-5" />
                <p>Manager Lucca cohérent avec boostcore.</p>
              </div>

              <div v-if="luccaManagerApplyError" class="alert alert-error text-sm">{{ luccaManagerApplyError }}</div>

              <!-- Proposition de managés (subordonnés) basée sur Lucca -->
              <template v-if="luccaSyncResult.managedProposal">
                <div
                  v-if="luccaSyncResult.managedProposal.status === 'changes_proposed' && luccaSyncResult.managedProposal.toAdd.length > 0"
                  class="alert alert-warning text-sm flex-col items-stretch gap-2"
                >
                  <div class="flex items-center gap-2">
                    <ExclamationTriangleIcon class="w-5 h-5" />
                    <p class="font-medium flex-1">Subordonnés Lucca à rattacher</p>
                  </div>
                  <ul class="space-y-1">
                    <li
                      v-for="u in luccaSyncResult.managedProposal.toAdd"
                      :key="u.uuid"
                      class="flex items-center gap-2"
                    >
                      <input
                        :id="`managed-${u.uuid}`"
                        v-model="luccaManagedSelected[u.uuid]"
                        type="checkbox"
                        class="checkbox checkbox-sm"
                      />
                      <label :for="`managed-${u.uuid}`" class="cursor-pointer flex-1">
                        <span class="font-semibold">{{ u.name }}</span>
                        <span class="text-base-content/60"> ({{ u.email }})</span>
                      </label>
                    </li>
                  </ul>
                  <div class="flex justify-end">
                    <button
                      class="btn btn-sm btn-primary"
                      :disabled="luccaManagedApplyLoading || !Object.values(luccaManagedSelected).some(Boolean)"
                      @click="applyLuccaManaged"
                    >
                      {{ luccaManagedApplyLoading ? 'Application…' : 'Appliquer la sélection' }}
                    </button>
                  </div>
                </div>

                <div
                  v-if="luccaSyncResult.managedProposal.toRemove.length > 0"
                  class="alert alert-info text-sm flex-col items-stretch gap-2"
                >
                  <div class="flex items-center gap-2">
                    <InformationCircleIcon class="w-5 h-5" />
                    <p class="font-medium flex-1">
                      Subordonnés actuels non listés sur Lucca
                    </p>
                  </div>
                  <p class="text-base-content/70">
                    Ces utilisateurs sont actuellement managés mais Lucca ne les rattache plus à ce manager.
                    Réaffecte-les manuellement si besoin.
                  </p>
                  <ul class="space-y-1">
                    <li
                      v-for="u in luccaSyncResult.managedProposal.toRemove"
                      :key="u.uuid"
                    >
                      <span class="font-semibold">{{ u.name }}</span>
                      <span class="text-base-content/60"> ({{ u.email }})</span>
                    </li>
                  </ul>
                </div>

                <div
                  v-if="luccaSyncResult.managedProposal.notInBoostcore.length > 0"
                  class="alert alert-info text-sm flex-col items-stretch gap-2"
                >
                  <div class="flex items-center gap-2">
                    <InformationCircleIcon class="w-5 h-5" />
                    <p class="font-medium flex-1">
                      Subordonnés Lucca non synchronisés en boostcore
                    </p>
                  </div>
                  <p class="text-base-content/70">
                    Ces utilisateurs n'ont pas de compte boostcore. Crée-les pour pouvoir les rattacher.
                  </p>
                  <ul class="space-y-1">
                    <li
                      v-for="u in luccaSyncResult.managedProposal.notInBoostcore"
                      :key="u.luccaId"
                    >
                      <span class="font-semibold">{{ u.name }}</span>
                      <span class="text-base-content/60"> ({{ u.email }})</span>
                    </li>
                  </ul>
                </div>

                <div
                  v-if="luccaSyncResult.managedProposal.status === 'all_correct'"
                  class="alert alert-success text-sm"
                >
                  <CheckCircleIcon class="w-5 h-5" />
                  <p>Subordonnés cohérents avec Lucca.</p>
                </div>
              </template>

              <div v-if="luccaManagedApplyError" class="alert alert-error text-sm">{{ luccaManagedApplyError }}</div>
            </template>
          </div>

          <div class="border-t pt-4 mt-4 space-y-3">
            <div class="flex items-center justify-between gap-2">
              <h4 class="font-medium text-sm">Synchronisation Odoo</h4>
              <button
                class="btn btn-outline btn-sm gap-1"
                :disabled="odooSyncLoading"
                @click="syncOdoo"
              >
                <ArrowPathIcon class="w-4 h-4" :class="{ 'animate-spin': odooSyncLoading }" />
                {{ odooSyncLoading ? 'Synchronisation…' : 'Sync Odoo' }}
              </button>
            </div>

            <div v-if="odooSyncError" class="alert alert-error text-sm">{{ odooSyncError }}</div>

            <template v-if="odooSyncResult">
              <div
                v-if="odooSyncResult.foundOnOdoo && odooSyncResult.odooUser"
                class="alert alert-success text-sm"
              >
                <CheckCircleIcon class="w-5 h-5" />
                <div>
                  <p class="font-medium">Utilisateur synchronisé avec Odoo</p>
                  <p>
                    {{ odooSyncResult.odooUser.name || odooSyncResult.odooUser.email }}
                    <span class="text-base-content/60">
                      (user #{{ odooSyncResult.odooUser.id }}<span v-if="odooSyncResult.odooUser.partnerId">, partner #{{ odooSyncResult.odooUser.partnerId }}</span>)
                    </span>
                  </p>
                </div>
              </div>

              <div
                v-else
                class="alert alert-error text-sm"
              >
                <XCircleIcon class="w-5 h-5" />
                <div class="flex-1">
                  <p class="font-medium">Utilisateur non trouvé sur Odoo</p>
                  <p>Aucun compte Odoo ne correspond à cet email.</p>
                </div>
              </div>
            </template>
          </div>
        </div>

        <div v-show="activeTab === 'manages'" class="space-y-4 overflow-x-auto">
          <div v-if="addManagedUserOptions.length" class="flex flex-wrap items-end gap-2">
            <SelectInput
              v-model="selectedUserToAddAsManaged"
              label="Ajouter un managé"
              :options="addManagedUserOptions"
              placeholder="Sélectionner un utilisateur…"
              class="flex-1 min-w-48"
            />
            <button
              class="btn btn-primary btn-sm"
              :disabled="!selectedUserToAddAsManaged"
              @click="addManagedUser"
            >
              Ajouter
            </button>
          </div>
          <div v-if="!(currentUser.managedUsers?.length)" class="text-base-content/70">
            Aucun managé
          </div>
          <ul v-else class="menu bg-base-200 rounded-lg min-w-full w-fit">
            <li v-for="m in currentUser.managedUsers" :key="m.uuid">
              <a
                class="grid grid-cols-[1.5fr_2fr_80px_20px] items-center gap-4 cursor-pointer"
                @click="openUser(m)"
              >
                <span class="font-medium truncate">{{ adminStore.getUserLabel(m) }}</span>
                <span class="text-sm text-base-content/70 truncate">{{ m.email }}</span>
                <span
                  class="badge badge-sm"
                  :class="m.status === 'ACTIVE' ? 'badge-success' : 'badge-error'"
                >
                  {{ m.status }}
                </span>
                <ChevronRightIcon class="w-4 h-4 justify-self-end" />
              </a>
            </li>
          </ul>
        </div>

        <div v-show="activeTab === 'permissions'" class="space-y-4">
          <p class="text-sm text-base-content/70">
            Les permissions sont attribuées via des groupes de rôles. Affecte ou désaffecte des groupes.
          </p>
          <div v-if="userRoleGroups.length" class="space-y-3">
            <div
              v-for="rg in userRoleGroups"
              :key="rg.uuid"
              class="p-3 bg-base-200 rounded-lg"
            >
              <div class="flex items-start justify-between gap-2">
                <div>
                  <p class="font-semibold">{{ rg.name }}</p>
                  <p v-if="rg.description" class="text-xs text-base-content/60 mt-0.5">{{ rg.description }}</p>
                </div>
                <button
                  type="button"
                  class="btn btn-ghost btn-xs text-error"
                  title="Désaffecter ce groupe"
                  @click="removeGroup(rg.uuid)"
                >
                  Désaffecter
                </button>
              </div>
              <div class="flex flex-wrap gap-1 mt-2">
                <span
                  v-for="p in rg.permissions"
                  :key="p.code"
                  class="badge badge-sm badge-ghost"
                  :title="p.description || undefined"
                >
                  {{ p.code }}
                </span>
              </div>
            </div>
          </div>
          <div v-else class="text-base-content/70">
            Aucun groupe affecté
          </div>
          <div v-if="availableRoleGroups.length" class="flex flex-wrap gap-1 pt-2 border-t border-base-200">
            <span class="text-sm font-medium w-full">Affecter un groupe :</span>
            <button
              v-for="g in availableRoleGroups"
              :key="g.uuid"
              class="btn btn-sm btn-outline"
              :title="g.permissions.map((p) => p.code).join(', ')"
              @click="addGroup(g.uuid)"
            >
              + {{ g.name }}
            </button>
          </div>
        </div>

        <div v-show="activeTab === 'featureflags'" class="space-y-4">
          <div v-if="isLoadingFeatureFlags" class="py-8 text-center">
            <span class="loading loading-spinner loading-lg" />
          </div>
          <template v-else>
            <p class="text-sm text-base-content/70">
              Affecte ou retire des feature flags pour cet utilisateur.
              <router-link
                v-if="boostCoreStore.include('SUPER_ADMIN')"
                to="/dashboard/admin/featureflag"
                class="link link-primary"
              >
                Gérer les flags
              </router-link>
            </p>

            <div v-if="featureFlagError" class="alert alert-error text-sm">{{ featureFlagError }}</div>

            <div v-if="userFeatureFlags.length" class="space-y-2">
              <div
                v-for="f in userFeatureFlags"
                :key="f.assignmentUuid"
                class="p-3 bg-base-200 rounded-lg flex items-start justify-between gap-2"
              >
                <div class="flex-1">
                  <div class="flex items-center gap-2">
                    <span class="font-mono font-semibold">{{ f.code }}</span>
                    <span
                      class="badge badge-xs"
                      :class="{
                        'badge-success': f.status === 'ACTIVE',
                        'badge-ghost': f.status === 'INACTIVE',
                        'badge-error': f.status === 'DEPRECATED',
                      }"
                    >
                      {{ f.status }}
                    </span>
                  </div>
                  <p v-if="f.description" class="text-xs text-base-content/60 mt-0.5">{{ f.description }}</p>
                  <p class="text-xs text-base-content/50 mt-0.5">Affecté le {{ formatDate(f.grantedAt) }}</p>
                </div>
                <button
                  type="button"
                  class="btn btn-ghost btn-xs text-error"
                  title="Retirer ce feature flag"
                  @click="unassignFeatureFlag(f.featureFlagUuid)"
                >
                  Retirer
                </button>
              </div>
            </div>
            <div v-else class="text-base-content/70">
              Aucun feature flag affecté
            </div>

            <div v-if="availableFlagsToAssign.length" class="flex flex-wrap items-end gap-2 pt-2 border-t border-base-200">
              <SelectInput
                v-model="selectedFlagToAssign"
                label="Affecter un feature flag"
                :options="availableFlagsToAssign"
                placeholder="Sélectionner un flag actif…"
                class="flex-1 min-w-48"
              />
              <button
                class="btn btn-primary btn-sm"
                :disabled="!selectedFlagToAssign || assigningFlag"
                @click="assignFeatureFlag"
              >
                {{ assigningFlag ? 'Affectation…' : 'Affecter' }}
              </button>
            </div>
            <div v-else-if="allFeatureFlags.length" class="text-sm text-base-content/60">
              Tous les feature flags actifs sont déjà affectés.
            </div>
          </template>
        </div>

        <div v-show="activeTab === 'tokens'" class="overflow-x-auto max-h-60">
          <div class="flex justify-end mb-2">
            <button
              v-if="currentUser.accessTokens.some((t) => isTokenActive(t))"
              class="btn btn-error btn-xs"
              @click="askRevokeAllTokens(currentUser.uuid)"
            >
              Tout révoquer
            </button>
          </div>
          <table class="table table-xs">
            <thead>
              <tr>
                <th>Type</th>
                <th>Émis le</th>
                <th>Expire</th>
                <th>Statut</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="t in currentUser.accessTokens" :key="t.uuid">
                <td>{{ t.tokenType }}</td>
                <td>{{ formatDate(t.issuedAt) }}</td>
                <td>{{ formatDate(t.expiresAt) }}</td>
                <td>
                  <span
                    :class="isTokenActive(t) ? 'badge badge-success' : (isTokenRevoked(t) ? 'badge badge-error' : 'badge badge-warning')"
                  >
                    {{ tokenStatus(t) }}
                  </span>
                </td>
                <td>
                  <button
                    v-if="isTokenActive(t)"
                    class="btn btn-ghost btn-xs text-error"
                    title="Révoquer"
                    @click="askRevokeToken(currentUser.uuid, t.uuid)"
                  >
                    <NoSymbolIcon class="w-4 h-4" />
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Password tab -->
        <div v-show="activeTab === 'password'" class="space-y-4">
          <div v-if="isLoadingPassword" class="py-8 text-center">
            <span class="loading loading-spinner loading-lg"></span>
          </div>
          <template v-else>
            <!-- Auth modes -->
            <div class="flex flex-wrap gap-2 mb-4">
              <span v-if="currentUser?.m365Oid" class="badge badge-info gap-1">
                Microsoft 365
              </span>
              <span v-if="passwordInfo?.hasPassword" class="badge badge-success gap-1">
                <KeyIcon class="w-3 h-3" />
                Mot de passe actif
              </span>
              <span v-else class="badge badge-ghost gap-1">
                Pas de mot de passe
              </span>
            </div>

            <!-- Password details -->
            <div v-if="passwordInfo?.hasPassword" class="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span class="text-base-content/60">Défini le :</span>
                <span class="ml-1 font-medium">{{ formatDate(passwordInfo.passwordSetAt) }}</span>
              </div>
              <div>
                <span class="text-base-content/60">Dernière connexion :</span>
                <span class="ml-1 font-medium">{{ formatDate(passwordInfo.lastSuccessAt) }}</span>
              </div>
              <div>
                <span class="text-base-content/60">Tentatives échouées :</span>
                <span class="ml-1 font-medium" :class="{ 'text-error': passwordInfo.failedAttempts >= 5 }">
                  {{ passwordInfo.failedAttempts }}
                </span>
              </div>
              <div v-if="passwordInfo.lastFailedAt">
                <span class="text-base-content/60">Dernier échec :</span>
                <span class="ml-1 font-medium">{{ formatDate(passwordInfo.lastFailedAt) }}</span>
              </div>
              <div v-if="passwordInfo.lockedUntil">
                <span class="text-base-content/60">Verrouillé jusqu'au :</span>
                <span class="ml-1 font-medium text-error">{{ formatDate(passwordInfo.lockedUntil) }}</span>
              </div>
              <div v-if="passwordInfo.mustRotate">
                <span class="badge badge-warning">Rotation obligatoire</span>
              </div>
            </div>

            <!-- Send reset link -->
            <div class="border-t pt-4 mt-4">
              <div class="flex items-center gap-3">
                <button
                  class="btn btn-outline btn-sm gap-1"
                  :class="{ 'loading': sendingReset }"
                  :disabled="sendingReset"
                  @click="sendResetToUser"
                >
                  <EnvelopeIcon class="w-4 h-4" />
                  {{ sendingReset ? 'Envoi...' : 'Envoyer un lien de réinitialisation' }}
                </button>
                <span v-if="resetSent" class="text-success text-sm">Email envoyé</span>
              </div>
            </div>

            <!-- Reset tokens -->
            <div v-if="resetTokens.length" class="border-t pt-4 mt-4">
              <h4 class="font-medium text-sm mb-2">Demandes de réinitialisation</h4>
              <div class="overflow-x-auto max-h-40">
                <table class="table table-xs">
                  <thead>
                    <tr>
                      <th>Demandé le</th>
                      <th>Expire</th>
                      <th>Raison</th>
                      <th>Statut</th>
                      <th>IP</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="t in resetTokens" :key="t.uuid">
                      <td>{{ formatDate(t.requestedAt) }}</td>
                      <td>{{ formatDate(t.expiresAt) }}</td>
                      <td>{{ t.requestReason ?? '—' }}</td>
                      <td>
                        <span v-if="t.usedAt" class="badge badge-success badge-xs">Utilisé</span>
                        <span v-else-if="t.revokedAt" class="badge badge-error badge-xs">Révoqué</span>
                        <span v-else-if="new Date(t.expiresAt) < new Date()" class="badge badge-ghost badge-xs">Expiré</span>
                        <span v-else class="badge badge-warning badge-xs">En attente</span>
                      </td>
                      <td class="text-xs font-mono">{{ t.requestIp ?? '—' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </template>
        </div>

        <!-- Brevo emails tab -->
        <div v-show="activeTab === 'brevo'" class="space-y-4">
          <div class="flex items-center justify-between">
            <h4 class="font-medium">Emails envoyés via Brevo</h4>
            <span class="text-sm text-base-content/60">{{ brevoCount }} email(s)</span>
          </div>
          <div v-if="isLoadingBrevo" class="py-8 text-center">
            <span class="loading loading-spinner loading-lg"></span>
          </div>
          <div v-else-if="brevoEmails.length === 0" class="py-8 text-center text-base-content/60">
            Aucun email trouvé.
          </div>
          <div v-else class="overflow-x-auto max-h-60">
            <table class="table table-xs">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Sujet</th>
                  <th>Statut</th>
                  <th>Message ID</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="e in brevoEmails" :key="e.uuid">
                  <td>{{ formatDate(e.date) }}</td>
                  <td>{{ e.subject ?? '—' }}</td>
                  <td>
                    <span class="badge badge-xs" :class="{
                      'badge-success': e.event === 'delivered',
                      'badge-info': e.event === 'opened' || e.event === 'clicks',
                      'badge-error': e.event === 'hardBounces' || e.event === 'softBounces',
                      'badge-ghost': !e.event || e.event === 'requests',
                    }">{{ e.event ?? 'envoyé' }}</span>
                  </td>
                  <td class="font-mono text-xs">{{ e.messageId ?? '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-show="activeTab === 'metadata'" class="space-y-4">
          <div v-if="!metadataJson" class="text-base-content/70">
            Aucune metadata
          </div>
          <pre
            v-else
            class="p-4 bg-base-200 rounded-lg overflow-x-auto text-sm font-mono max-h-80 overflow-y-auto"
          ><code>{{ metadataJson }}</code></pre>
        </div>
        <div v-show="activeTab === 'preferences'" class="space-y-4">
          <div v-if="!preferencesJson" class="text-base-content/70">
            Aucune préférences
          </div>
          <pre
            v-else
            class="p-4 bg-base-200 rounded-lg overflow-x-auto text-sm font-mono max-h-80 overflow-y-auto"
          ><code>{{ preferencesJson }}</code></pre>
        </div>
      </div>

      <div class="modal-action">
        <button class="btn" @click="closeModal">Fermer</button>
        <button
          class="btn btn-primary"
          :disabled="saving"
          @click="saveUser"
        >
          {{ saving ? 'Enregistrement…' : 'Enregistrer' }}
        </button>
      </div>
    </div>
    <div class="modal-backdrop" @click="closeModal" />
  </div>

  <div class="modal" :class="{ 'modal-open': showAddModal }">
    <div class="modal-box max-w-lg">
      <h3 class="font-bold text-lg flex items-center gap-2">
        <PlusIcon class="w-6 h-6" />
        Ajouter un utilisateur
      </h3>

      <div class="mt-4 space-y-4">
        <div class="form-control">
          <label class="label"><span class="label-text">Email</span></label>
          <div class="join">
            <input
              v-model="addEmail"
              type="email"
              placeholder="utilisateur@domaine.fr"
              class="input input-bordered input-sm join-item flex-1"
              :disabled="isLookingUp || isCreating"
              @keydown.enter.prevent="doLookupLucca"
            />
            <button
              class="btn btn-sm btn-primary join-item"
              :disabled="!addEmail.trim() || isLookingUp"
              @click="doLookupLucca"
            >
              <MagnifyingGlassIcon class="w-4 h-4" />
              {{ isLookingUp ? 'Recherche…' : 'Chercher sur Lucca' }}
            </button>
          </div>
        </div>

        <div v-if="lookupError" class="alert alert-error text-sm">{{ lookupError }}</div>

        <template v-if="lookupResult">
          <div v-if="lookupResult.existingUser" class="alert alert-warning">
            <div class="flex-1">
              <p class="font-medium">Cet utilisateur existe déjà en base</p>
              <p class="text-sm">
                {{ lookupResult.existingUser.firstName }} {{ lookupResult.existingUser.lastName }}
                — {{ lookupResult.existingUser.email }}
              </p>
            </div>
            <button class="btn btn-sm btn-primary" @click="openExistingUserFromLookup">
              Ouvrir la fiche
            </button>
          </div>

          <template v-else>
            <div
              v-if="lookupResult.foundOnLucca"
              class="alert alert-success text-sm"
            >
              <CheckCircleIcon class="w-5 h-5" />
              <div>
                <p class="font-medium">Trouvé sur Lucca</p>
                <p>
                  {{ lookupResult.firstName }} {{ lookupResult.lastName }}
                  <span v-if="lookupResult.employeeNumber" class="text-base-content/60">
                    (matricule {{ lookupResult.employeeNumber }})
                  </span>
                </p>
              </div>
            </div>
            <div v-else class="alert alert-info text-sm">
              <XCircleIcon class="w-5 h-5" />
              <div>
                <p class="font-medium">Non trouvé sur Lucca</p>
                <p>Renseigne le prénom et le nom pour créer l'utilisateur manuellement.</p>
              </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div class="form-control">
                <label class="label"><span class="label-text">Prénom</span></label>
                <input
                  v-model="addFirstName"
                  type="text"
                  class="input input-bordered input-sm"
                  :disabled="isCreating"
                />
              </div>
              <div class="form-control">
                <label class="label"><span class="label-text">Nom</span></label>
                <input
                  v-model="addLastName"
                  type="text"
                  class="input input-bordered input-sm"
                  :disabled="isCreating"
                />
              </div>
            </div>

            <div v-if="createError" class="alert alert-error text-sm">{{ createError }}</div>
          </template>
        </template>
      </div>

      <div class="modal-action">
        <button class="btn" :disabled="isCreating" @click="closeAddModal">Annuler</button>
        <button
          v-if="lookupResult && !lookupResult.existingUser"
          class="btn btn-primary"
          :disabled="isCreating || !addEmail.trim() || (!lookupResult.foundOnLucca && (!addFirstName.trim() || !addLastName.trim()))"
          @click="doCreateUser"
        >
          {{ isCreating ? 'Création…' : 'Créer' }}
        </button>
      </div>
    </div>
    <div class="modal-backdrop" @click="closeAddModal" />
  </div>

  <ConfirmModal
    :show-modal="showRevokeModal"
    title="Révoquer le token"
    body="Êtes-vous sûr de vouloir révoquer ce token ? L'utilisateur devra se reconnecter."
    confirm-text="Révoquer"
    confirm-variant="error"
    @confirm="confirmRevokeToken"
    @cancel="showRevokeModal = false; tokenToRevoke = null"
  />

  <ConfirmModal
    :show-modal="showRevokeAllModal"
    title="Révoquer toutes les sessions"
    body="Cette action révoque tous les access tokens actifs de cet utilisateur. Toutes les sessions ouvertes seront déconnectées et l'utilisateur devra se reconnecter."
    confirm-text="Tout révoquer"
    confirm-variant="error"
    @confirm="confirmRevokeAllTokens"
    @cancel="showRevokeAllModal = false; userToRevokeAll = null"
  />
</template>
